/**
 * /finance/approvals — Approvals Inbox
 * Steve's morning-glance authorize-here queue. Read-only surface;
 * authorize actions deep-link into the existing pay-runs cockpit
 * where the canonical write paths live.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldAlert, ChevronRight } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { usePayRunViews } from "@/data/finance/payRun/usePayRunViews";
import { getPendingFieldAdds } from "@/data/finance/payRun/fieldAdds";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import { deriveAllDisputes } from "@/data/contractor/networkExecution/dispute/dispute";
import {
  buildApprovalsInbox,
  type ApprovalItem,
  type ApprovalKind,
  type ApprovalUrgency,
} from "@/data/finance/financeWorkbenchSelectors";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";

const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;

const KIND_LABEL: Record<ApprovalKind, string> = {
  "pay-run-held": "Held pay run",
  "field-add": "Field-add",
  "holdback-release": "Holdback",
  "dispute-open": "Dispute",
};

function UrgencyDot({ urgency }: { urgency: ApprovalUrgency }) {
  const cls =
    urgency === "critical" ? "bg-destructive"
    : urgency === "warn" ? "bg-warning"
    : "bg-primary/60";
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${cls}`} />;
}

function ApprovalRow({ item }: { item: ApprovalItem }) {
  return (
    <Link
      to={item.href}
      className="grid grid-cols-[1fr_120px_100px_110px] items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors border-b border-border/40 last:border-0 group"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <UrgencyDot urgency={item.urgency} />
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
            {KIND_LABEL[item.kind]}
          </span>
        </div>
        <p className="text-[13px] font-medium text-foreground truncate">{item.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{item.subtitle}</p>
      </div>
      <div className="text-right">
        <p className="text-[13px] font-semibold tabular-nums">{item.amount > 0 ? fmtCompact(item.amount) : "—"}</p>
      </div>
      <div className="text-right">
        <p className={`text-[12px] tabular-nums ${item.ageDays >= 7 ? "text-destructive font-medium" : item.ageDays >= 3 ? "text-warning" : "text-muted-foreground"}`}>
          {item.ageDays === 0 ? "today" : `${item.ageDays}d`}
        </p>
      </div>
      <div className="text-right text-[12px] text-primary group-hover:underline inline-flex items-center justify-end gap-1">
        {item.cta} <ChevronRight className="h-3 w-3" />
      </div>
    </Link>
  );
}

export default function ApprovalsInboxPage() {
  const appData = useAppData();
  const { jobs, holdbackLedger, disputeEvents } = appData;
  const fieldAddSubmissions: any[] = (appData as any).fieldAddSubmissions ?? [];
  const { heldRows } = usePayRunViews();
  const fin$ = useFinancialPermissions();

  const items = useMemo(() => {
    const pendingFieldAdds = getPendingFieldAdds(fieldAddSubmissions);
    const holdbackStates = deriveAllHoldbackStates(holdbackLedger);
    const disputeStates = deriveAllDisputes(disputeEvents);
    return buildApprovalsInbox({ heldRows, pendingFieldAdds, holdbackStates, disputeStates, jobs });
  }, [heldRows, fieldAddSubmissions, holdbackLedger, disputeEvents, jobs]);

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-muted-foreground max-w-md">Approvals require financial permissions.</p>
        </div>
      </CockpitPage>
    );
  }

  const grouped = {
    critical: items.filter((i) => i.urgency === "critical"),
    warn: items.filter((i) => i.urgency === "warn"),
    info: items.filter((i) => i.urgency === "info"),
  };
  const totalDollars = items.reduce((s, i) => s + i.amount, 0);

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Approvals Inbox</h1>
        <p className="text-[12px] text-muted-foreground mt-1">
          {items.length} pending · {fmtCompact(totalDollars)} authorize value
          {grouped.critical.length > 0 ? ` · ${grouped.critical.length} critical` : ""}
        </p>
      </header>

      <FinanceNavStrip />

      {items.length === 0 ? (
        <div className="bg-background border border-border/60 flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 className="h-8 w-8 text-success mb-3" />
          <p className="text-sm font-medium text-foreground">Inbox zero</p>
          <p className="text-[12px] text-muted-foreground mt-1">Nothing waiting on you right now.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(["critical", "warn", "info"] as const).map((tier) => {
            const list = grouped[tier];
            if (list.length === 0) return null;
            const label = tier === "critical" ? "Critical" : tier === "warn" ? "Needs review" : "FYI";
            const tone = tier === "critical" ? "text-destructive" : tier === "warn" ? "text-warning" : "text-muted-foreground";
            return (
              <section key={tier} className="bg-background border border-border/60">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
                  <p className={`text-[11px] uppercase tracking-[0.16em] font-medium ${tone}`}>{label}</p>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {list.length} · {fmtCompact(list.reduce((s, i) => s + i.amount, 0))}
                  </span>
                </div>
                <div>
                  {list.map((it) => <ApprovalRow key={it.id} item={it} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </CockpitPage>
  );
}
